import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectHasShownSelectProductNotification,
  selectIsProductConfirmationMode,
  selectSelectedProduct,
  selectSelectedProductVariants,
} from '../../store/selectors';
import { setIsProductConfirmationMode } from '../../store/slices/confirmationSlice';
import { setHasShownSelectProductNotification } from '../../store/slices/gameSlice';
import { showNotification } from '../../store/slices/notificationSlice';
import { formatPrice, formatTime } from '../../utils/Formatting';
import { PRIZE_AMOUNT } from '../../constants/gameConfig';
import { useSettingsRedux } from '../../hooks/useSettingsRedux';
import walletIcon from '../../assets/wallet.png';
import diamondIcon from '../../assets/diamond.png';
import './StatusBar.css';
import { StatusBarProps } from './StatusBar';

type DefaultGameBarProps = Pick<
  StatusBarProps,
  | 'walletAmount'
  | 'claimedPrizes'
  | 'totalPrizes'
  | 'showPrizeAnimation'
  | 'walletPulse'
  | 'walletRef'
  | 'selectedProduct'
  | 'timeRemaining'
  | 'rootPath'
>;

const DefaultGameBar = ({
  walletAmount,
  claimedPrizes,
  totalPrizes,
  showPrizeAnimation,
  walletPulse,
  walletRef,
  selectedProduct,
  timeRemaining,
  rootPath = '',
}: DefaultGameBarProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { settings } = useSettingsRedux();

  const isProductConfirmationMode = useAppSelector(selectIsProductConfirmationMode);
  const selectedProductFromRedux = useAppSelector(selectSelectedProduct);
  const hasShownSelectProductNotification = useAppSelector(selectHasShownSelectProductNotification);
  const selectedProductVariants = useAppSelector(selectSelectedProductVariants);

  const currentSelectedProduct = location.pathname.includes('/product/') ? selectedProductFromRedux : selectedProduct;
  const productPathPrefix = rootPath ? `${rootPath}/product` : '/product';
  const isProductView = location.pathname.startsWith(productPathPrefix);
  const allPrizesCollected = claimedPrizes === totalPrizes && totalPrizes > 0;

  const hasAllVariantsSelected = () => {
    if (!currentSelectedProduct?.options || currentSelectedProduct.options.length === 0) {
      return true; // No variants required
    }
    if (!selectedProductVariants || Object.keys(selectedProductVariants).length === 0) {
      return false; // No variants selected at all
    }
    return currentSelectedProduct.options.every((option) => {
      const optionName = option.name;
      return (
        selectedProductVariants[optionName] ||
        selectedProductVariants[optionName.toLowerCase()] ||
        selectedProductVariants[optionName.charAt(0).toUpperCase() + optionName.slice(1).toLowerCase()]
      );
    });
  };

  const handleChooseProductClick = () => {
    if (!currentSelectedProduct) {
      console.warn('Choose product clicked without a selected product.');
      return;
    }

    if (currentSelectedProduct.options.length > 0 && !hasAllVariantsSelected()) {
      const sizeChooseText = settings?.translations?.product?.sizeChoose ?? 'Valitse koko';
      dispatch(showNotification({ message: sizeChooseText, yPosition: 60 }));
      return;
    }

    dispatch(setIsProductConfirmationMode(true));
  };

  useEffect(() => {
    if (allPrizesCollected && !isProductConfirmationMode && !hasShownSelectProductNotification) {
      const timer = setTimeout(() => {
        const nextTranslation = settings?.translations?.feed?.collectable?.next ?? 'Use your discount on any product';
        dispatch(showNotification({ message: nextTranslation, yPosition: 60 }));
        dispatch(setHasShownSelectProductNotification(true));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [allPrizesCollected, isProductConfirmationMode, dispatch, hasShownSelectProductNotification, settings]);

  const renderProductActionButton = allPrizesCollected && isProductView;

  return (
    <>
      <div className={`playmerce-game-statusbar-wallet ${walletPulse ? 'pulse' : ''}`} ref={walletRef}>
        <img src={walletIcon} alt="Wallet" className="wallet-icon" />
        <p className="wallet-amount">{formatPrice(walletAmount.toString(), 'EUR')}</p>
        {showPrizeAnimation && <div className="prize-animation">+{PRIZE_AMOUNT} €</div>}
      </div>

      {renderProductActionButton ? (
        <button className="choose-product-button" onClick={handleChooseProductClick}>
          {settings?.translations?.product?.apply ?? 'Apply'}
        </button>
      ) : (
        <div className="playmerce-game-statusbar-prizes">
          <img src={diamondIcon} alt="Prizes" className="prize-icon" />
          <span className="claimed-prizes-counter">
            {claimedPrizes}/{totalPrizes}
          </span>
        </div>
      )}

      <div className="playmerce-game-statusbar-timer">
        <p className="timer-text">{formatTime(timeRemaining)}</p>
      </div>
    </>
  );
};

export default DefaultGameBar;
